import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronUp, Search, Filter, Layers, FileText, X, SlidersHorizontal, Trash2, Loader2 } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import ExportButton from '../common/ExportButton';
import ViewToggle from '../common/ViewToggle';
import { exportDataToExcel, getTimestamp } from '../../utils/exportUtils';
import { PERSONA_COLORS } from '../../utils/personaColors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERSONA_COLOR_CLASSES = {
  'Model Clients': 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500',
  'Stable Performers': 'bg-blue-500/10 border-blue-500/30 text-blue-500',
  'Premium Service': 'bg-purple-500/10 border-purple-500/30 text-purple-500',
  'Scale Up': 'bg-cyan-500/10 border-cyan-500/30 text-cyan-500',
  'Optimize': 'bg-amber-500/10 border-amber-500/30 text-amber-500',
  'Turnaround': 'bg-orange-500/10 border-orange-500/30 text-orange-500',
  'At Risk': 'bg-rose-500/10 border-rose-500/30 text-rose-500',
  'Critical Review': 'bg-red-500/10 border-red-500/30 text-red-500',
  'Unknown': 'bg-slate-500/10 border-slate-500/30 text-slate-500'
};

const FilterDropdown = ({ label, options, selected, onChange, isDark }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggle = (val) => {
    const next = new Set(selected);
    if (next.has(val)) next.delete(val);
    else next.add(val);
    onChange(next);
  };

  return (
    <div ref={ref} className="relative">
      <button
        data-testid={`filter-dropdown-${label.toLowerCase().replace(/\s/g, '-')}`}
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all
          ${selected.size > 0
            ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600'
            : isDark ? 'bg-white/5 border-white/10 text-slate-400 hover:border-white/20' : 'bg-slate-50 border-slate-200 text-slate-500 hover:border-slate-300'
          }`}
      >
        {label}
        {selected.size > 0 && (
          <span className="bg-emerald-500 text-white text-[10px] px-1.5 py-0 rounded-full">{selected.size}</span>
        )}
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className={`absolute top-full left-0 mt-1 z-50 min-w-[180px] max-h-[260px] overflow-y-auto rounded-xl border shadow-xl
          ${isDark ? 'bg-[#1a1a1d] border-white/10' : 'bg-white border-slate-200'}`}>
          {selected.size > 0 && (
            <button
              onClick={() => { onChange(new Set()); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-xs font-medium border-b
                ${isDark ? 'text-rose-400 border-white/5 hover:bg-white/5' : 'text-rose-500 border-slate-100 hover:bg-slate-50'}`}
            >
              Clear all
            </button>
          )}
          {options.map(opt => (
            <label key={opt} className={`flex items-center gap-2 px-3 py-2 text-xs cursor-pointer
              ${isDark ? 'hover:bg-white/5 text-slate-300' : 'hover:bg-slate-50 text-slate-700'}`}>
              <input
                type="checkbox"
                checked={selected.has(opt)}
                onChange={() => toggle(opt)}
                className="rounded border-slate-300 text-emerald-500 focus:ring-emerald-500 w-3.5 h-3.5"
              />
              {opt}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const RangeFilter = ({ label, min, max, value, onChange, isDark, format = 'currency' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const hasFilter = value[0] !== min || value[1] !== max;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatVal = (v) => {
    if (format === 'currency') return `$${Math.round(v).toLocaleString()}`;
    if (format === 'percent') return `${(v * 100).toFixed(0)}%`;
    return v.toLocaleString();
  };

  return (
    <div ref={ref} className="relative">
      <button
        data-testid={`filter-range-${label.toLowerCase().replace(/\s/g, '-')}`}
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all
          ${hasFilter
            ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600'
            : isDark ? 'bg-white/5 border-white/10 text-slate-400 hover:border-white/20' : 'bg-slate-50 border-slate-200 text-slate-500 hover:border-slate-300'
          }`}
      >
        {label}
        {hasFilter && <span className="bg-emerald-500 text-white text-[10px] px-1 py-0 rounded-full">!</span>}
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className={`absolute top-full left-0 mt-1 z-50 w-[220px] rounded-xl border shadow-xl p-3 space-y-2
          ${isDark ? 'bg-[#1a1a1d] border-white/10' : 'bg-white border-slate-200'}`}>
          <div className="flex items-center justify-between">
            <span className={`text-[10px] font-semibold uppercase ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{label} Range</span>
            {hasFilter && (
              <button onClick={() => onChange([min, max])} className="text-[10px] text-rose-400 hover:text-rose-300">Reset</button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={format === 'percent' ? (value[0] * 100).toFixed(0) : Math.round(value[0])}
              onChange={e => {
                const v = format === 'percent' ? parseFloat(e.target.value) / 100 : parseFloat(e.target.value);
                onChange([isNaN(v) ? min : v, value[1]]);
              }}
              className={`w-full px-2 py-1.5 text-xs rounded-lg border
                ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-slate-50 border-slate-200 text-slate-900'}`}
              placeholder="Min"
            />
            <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>-</span>
            <input
              type="number"
              value={format === 'percent' ? (value[1] * 100).toFixed(0) : Math.round(value[1])}
              onChange={e => {
                const v = format === 'percent' ? parseFloat(e.target.value) / 100 : parseFloat(e.target.value);
                onChange([value[0], isNaN(v) ? max : v]);
              }}
              className={`w-full px-2 py-1.5 text-xs rounded-lg border
                ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-slate-50 border-slate-200 text-slate-900'}`}
              placeholder="Max"
            />
          </div>
          <div className={`text-[10px] text-center ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
            {formatVal(value[0])} - {formatVal(value[1])}
          </div>
        </div>
      )}
    </div>
  );
};

const DataTable = ({ data, category, onDeletePlans }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [sortField, setSortField] = useState('revWorkloadVariance');
  const [sortDir, setSortDir] = useState('desc');
  const [filter, setFilter] = useState('');
  const [viewMode, setViewMode] = useState('plan');
  const [groupData, setGroupData] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [personaFilter, setPersonaFilter] = useState(new Set());
  const [managerFilter, setManagerFilter] = useState(new Set());
  const [profitFilter, setProfitFilter] = useState('all'); // 'all', 'profitable', 'unprofitable'
  const [revenueRange, setRevenueRange] = useState([0, Infinity]);
  const [marginRange, setMarginRange] = useState([-Infinity, Infinity]);
  const [selectedPlans, setSelectedPlans] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (viewMode === 'group' && groupData.length === 0) {
      setLoadingGroups(true);
      axios.get(`${API}/plan-groups`)
        .then(res => setGroupData(res.data.groups || []))
        .catch(err => console.error('Failed to load plan groups:', err))
        .finally(() => setLoadingGroups(false));
    }
  }, [viewMode, groupData.length]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const transformedGroupData = groupData.map(g => ({
    id: g.basePlan,
    propertyName: g.primaryPropertyName || (g.propertyNames && g.propertyNames[0]) || '',
    manager: g.primaryManager || g.managers?.[0] || 'Unknown',
    managementFees: 0,
    revenue: g.totalRevenue,
    workloadCost: g.totalWorkload,
    messagesReceived: Math.round(g.totalMessageCost / 30),
    revWorkloadVariance: g.profit,
    margin: g.margin / 100,
    complianceScore: g.avgCompliance || 50,
    npsScore: g.avgNPS,
    persona: g.dominantPersona || 'Unknown',
    planCount: g.planCount,
    totalMeetings: g.totalMeetings,
    totalOccupiableLots: g.totalOccupiableLots || 0,
    revenuePerLot: g.revenuePerLot || 0,
    workloadPerLot: g.workloadPerLot || 0,
    marginPerLot: g.marginPerLot || 0
  }));

  const activeData = viewMode === 'plan' ? data : transformedGroupData;

  // Compute ranges and unique values for filters
  const { personas, managers, revMin, revMax, mrgMin, mrgMax } = useMemo(() => {
    const p = new Set(), m = new Set();
    let rMin = Infinity, rMax = -Infinity, mrMin = Infinity, mrMax = -Infinity;
    (activeData || []).forEach(item => {
      if (item.persona) p.add(item.persona);
      if (item.manager) m.add(item.manager);
      if (item.revenue < rMin) rMin = item.revenue;
      if (item.revenue > rMax) rMax = item.revenue;
      if (item.margin < mrMin) mrMin = item.margin;
      if (item.margin > mrMax) mrMax = item.margin;
    });
    return {
      personas: [...p].sort(),
      managers: [...m].sort(),
      revMin: rMin === Infinity ? 0 : rMin,
      revMax: rMax === -Infinity ? 100000 : rMax,
      mrgMin: mrMin === Infinity ? -1 : mrMin,
      mrgMax: mrMax === -Infinity ? 1 : mrMax
    };
  }, [activeData]);

  // Reset range filters when switching views or when data changes
  useEffect(() => {
    setRevenueRange([revMin, revMax]);
    setMarginRange([mrgMin, mrgMax]);
  }, [revMin, revMax, mrgMin, mrgMax]);

  const activeFilterCount = (personaFilter.size > 0 ? 1 : 0)
    + (managerFilter.size > 0 ? 1 : 0)
    + (profitFilter !== 'all' ? 1 : 0)
    + (revenueRange[0] !== revMin || revenueRange[1] !== revMax ? 1 : 0)
    + (marginRange[0] !== mrgMin || marginRange[1] !== mrgMax ? 1 : 0);

  const clearAllFilters = () => {
    setPersonaFilter(new Set());
    setManagerFilter(new Set());
    setProfitFilter('all');
    setRevenueRange([revMin, revMax]);
    setMarginRange([mrgMin, mrgMax]);
    setFilter('');
  };

  const toggleSelectPlan = (planId) => {
    setSelectedPlans(prev => {
      const next = new Set(prev);
      if (next.has(planId)) next.delete(planId);
      else next.add(planId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedPlans.size === filteredData.length) {
      setSelectedPlans(new Set());
    } else {
      setSelectedPlans(new Set(filteredData.map(r => r.id)));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedPlans.size === 0) return;
    setDeleting(true);
    try {
      await axios.post(`${API}/excluded-plans`, { planNumbers: [...selectedPlans] });
      setSelectedPlans(new Set());
      if (onDeletePlans) onDeletePlans();
    } catch (error) {
      console.error('Failed to exclude plans:', error);
    } finally {
      setDeleting(false);
    }
  };

  const filteredData = (activeData || [])
    .filter(item => {
      if (filter) {
        const f = filter.toLowerCase();
        if (!(item.id.toLowerCase().includes(f) || item.manager.toLowerCase().includes(f) || item.persona.toLowerCase().includes(f) || (item.propertyName || '').toLowerCase().includes(f)))
          return false;
      }
      if (personaFilter.size > 0 && !personaFilter.has(item.persona)) return false;
      if (managerFilter.size > 0 && !managerFilter.has(item.manager)) return false;
      if (profitFilter === 'profitable' && item.revWorkloadVariance < 0) return false;
      if (profitFilter === 'unprofitable' && item.revWorkloadVariance >= 0) return false;
      if (item.revenue < revenueRange[0] || item.revenue > revenueRange[1]) return false;
      if (item.margin !== null && item.margin !== undefined && (item.margin < marginRange[0] || item.margin > marginRange[1])) return false;
      return true;
    })
    .sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (aVal === null) return 1;
      if (bVal === null) return -1;
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <div className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-30 inline-block"><ChevronDown className="w-3 h-3"/></div>;
    return sortDir === 'asc' 
      ? <div className="w-3 h-3 ml-1 inline-block text-[#171717]"><ChevronUp className="w-3 h-3"/></div>
      : <div className="w-3 h-3 ml-1 inline-block text-[#171717]"><ChevronDown className="w-3 h-3"/></div>;
  };

  const planHeaders = [
    { label: 'Plan ID', key: 'id', align: 'left', format: 'string' },
    { label: 'Property Name', key: 'propertyName', align: 'left', format: 'string' },
    { label: 'Manager', key: 'manager', align: 'left', format: 'string' },
    { label: 'Lots', key: 'occupiableLots', align: 'center', format: 'number' },
    { label: 'Mgmt Fee', key: 'managementFees', align: 'right', format: 'currency' },
    { label: 'Total Revenue', key: 'revenue', align: 'right', format: 'currency' },
    { label: 'Workload Cost', key: 'workloadCost', align: 'right', format: 'currency' },
    { label: 'Emails', key: 'messagesReceived', align: 'center', format: 'number' },
    { label: 'Meetings', key: 'totalMeetings', align: 'center', format: 'number' },
    { label: 'Gross Profit', key: 'revWorkloadVariance', align: 'right', format: 'currency' },
    { label: 'Gross Margin', key: 'margin', align: 'right', format: 'percent' },
    { label: 'Rev/Lot', key: 'revenuePerLot', align: 'right', format: 'currency' },
    { label: 'Cost/Lot', key: 'workloadCostPerLot', align: 'right', format: 'currency' },
    { label: 'Margin/Lot', key: 'grossMarginPerLot', align: 'right', format: 'currency' },
    { label: 'Compliance', key: 'complianceScore', align: 'center', format: 'number' },
    { label: 'NPS', key: 'npsScore', align: 'center', format: 'number' },
    { label: 'Cohort', key: 'persona', align: 'left', format: 'string' },
  ];

  const groupHeaders = [
    { label: 'Plan Group', key: 'id', align: 'left', format: 'string' },
    { label: 'Property Name', key: 'propertyName', align: 'left', format: 'string' },
    { label: 'Manager', key: 'manager', align: 'left', format: 'string' },
    { label: 'Plans', key: 'planCount', align: 'center', format: 'number' },
    { label: 'Lots', key: 'totalOccupiableLots', align: 'center', format: 'number' },
    { label: 'Total Revenue', key: 'revenue', align: 'right', format: 'currency' },
    { label: 'Workload Cost', key: 'workloadCost', align: 'right', format: 'currency' },
    { label: 'Meetings', key: 'totalMeetings', align: 'center', format: 'number' },
    { label: 'Gross Profit', key: 'revWorkloadVariance', align: 'right', format: 'currency' },
    { label: 'Gross Margin', key: 'margin', align: 'right', format: 'percent' },
    { label: 'Rev/Lot', key: 'revenuePerLot', align: 'right', format: 'currency' },
    { label: 'Cost/Lot', key: 'workloadPerLot', align: 'right', format: 'currency' },
    { label: 'Margin/Lot', key: 'marginPerLot', align: 'right', format: 'currency' },
    { label: 'Compliance', key: 'complianceScore', align: 'center', format: 'number' },
    { label: 'Cohort', key: 'persona', align: 'left', format: 'string' },
  ];

  const headers = viewMode === 'plan' ? planHeaders : groupHeaders;

  const handleExportExcel = () => {
    const exportColumns = headers.map(h => ({
      header: h.label,
      key: h.key,
      format: h.format
    }));
    
    const filename = viewMode === 'plan' 
      ? `knight_data_${category.toLowerCase()}_${getTimestamp()}.xlsx`
      : `knight_plan_groups_${category.toLowerCase()}_${getTimestamp()}.xlsx`;
    
    exportDataToExcel(filteredData, exportColumns, filename);
  };

  if (viewMode === 'group' && loadingGroups) {
    return (
      <div className="rounded-[10px] border border-[#E5E5E5] p-12 flex items-center justify-center bg-white" style={{boxShadow: 'var(--card-shadow)'}}>
        <div className="w-8 h-8 border-3 border-[#E5E5E5] border-t-[#171717] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="rounded-[10px] border border-[#E5E5E5] overflow-hidden flex flex-col h-full bg-white" style={{boxShadow: 'var(--card-shadow)'}}>
      <div className="p-5 border-b border-[#E5E5E5] flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center">
              <Filter className="w-4 h-4 mr-2 text-[#525252]" />
              <h3 className="text-[15px] font-bold text-[#171717] font-heading">{category} Analysis</h3>
              <span className="ml-3 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-[#F5F5F5] text-[#737373]">
                {filteredData.length} {viewMode === 'group' ? 'groups' : 'records'}
              </span>
            </div>
            <ViewToggle viewMode={viewMode} onToggle={setViewMode} />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-[#A3A3A3]">← Scroll →</span>
            <button
              data-testid="toggle-filters-btn"
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border transition-all
                ${showFilters || activeFilterCount > 0
                  ? 'bg-[#171717] border-[#171717] text-white'
                  : 'bg-[#F5F5F5] border-[#E5E5E5] text-[#525252] hover:border-[#D4D4D4]'
                }`}
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              Filters
              {activeFilterCount > 0 && (
                <span className="bg-white text-[#171717] text-[10px] px-1.5 py-0 rounded-full font-bold">{activeFilterCount}</span>
              )}
            </button>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 transform -translate-y-1/2 text-[#A3A3A3]" />
              <input 
                type="text" 
                data-testid="table-search-input"
                placeholder={viewMode === 'group' ? "Search groups, managers..." : "Search plans, managers..."} 
                className="pl-9 pr-4 py-2 text-sm border border-[#E5E5E5] rounded-lg bg-[#F5F5F5] text-[#171717] placeholder-[#A3A3A3] focus:outline-none focus:border-[#171717] focus:ring-1 focus:ring-[#171717]/10 w-56"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
            </div>
            <ExportButton 
              onExportExcel={handleExportExcel}
              showPDF={false}
              excelLabel="Export Data"
            />
          </div>
        </div>

        {showFilters && (
          <div data-testid="advanced-filter-bar" className="flex flex-wrap items-center gap-2 pt-3 border-t border-[#F5F5F5]">
            <span className="text-[10px] font-semibold uppercase tracking-wider mr-1 text-[#A3A3A3]">Filter by:</span>

            <FilterDropdown label="Cohort" options={personas} selected={personaFilter} onChange={setPersonaFilter} isDark={isDark} />
            <FilterDropdown label="Manager" options={managers} selected={managerFilter} onChange={setManagerFilter} isDark={isDark} />

            <div className="flex items-center gap-1">
              {['all', 'profitable', 'unprofitable'].map(opt => (
                <button
                  key={opt}
                  data-testid={`profit-filter-${opt}`}
                  onClick={() => setProfitFilter(opt)}
                  className={`px-2.5 py-1.5 text-[11px] font-medium rounded-lg border transition-all capitalize
                    ${profitFilter === opt
                      ? opt === 'profitable' ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                        : opt === 'unprofitable' ? 'bg-red-50 border-red-200 text-red-600'
                        : 'bg-[#171717] border-[#171717] text-white'
                      : 'bg-[#F5F5F5] border-[#E5E5E5] text-[#737373] hover:border-[#D4D4D4]'
                    }`}
                >
                  {opt}
                </button>
              ))}
            </div>

            <RangeFilter label="Revenue" min={revMin} max={revMax} value={revenueRange} onChange={setRevenueRange} isDark={isDark} format="currency" />
            <RangeFilter label="Margin" min={mrgMin} max={mrgMax} value={marginRange} onChange={setMarginRange} isDark={isDark} format="percent" />

            {activeFilterCount > 0 && (
              <button
                data-testid="clear-all-filters-btn"
                onClick={clearAllFilters}
                className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium text-red-500 rounded-lg border border-red-200 bg-red-50 hover:bg-red-100 transition-all ml-1"
              >
                <X className="w-3 h-3" /> Clear All
              </button>
            )}
          </div>
        )}
      </div>

      {/* Bulk delete toolbar */}
      {selectedPlans.size > 0 && (
        <div className="px-5 py-2.5 border-b border-[#E5E5E5] flex items-center justify-between bg-red-50" data-testid="bulk-delete-toolbar">
          <span className="text-xs font-medium text-red-700">{selectedPlans.size} plan(s) selected</span>
          <div className="flex gap-2">
            <button
              data-testid="clear-plan-selection-btn"
              onClick={() => setSelectedPlans(new Set())}
              className="text-xs px-2.5 py-1.5 rounded-[6px] text-[#525252] hover:bg-red-100 transition-colors"
            >
              Clear
            </button>
            <button
              data-testid="delete-plans-btn"
              onClick={handleDeleteSelected}
              disabled={deleting}
              className="text-xs px-3 py-1.5 rounded-[6px] bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center gap-1.5 disabled:opacity-50"
            >
              {deleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" strokeWidth={1.5} />}
              Remove from All Views
            </button>
          </div>
        </div>
      )}

      <div className="table-scroll-wrapper">
        <div className="table-scroll-container pb-2 relative">
          <table className="w-full text-sm text-left border-collapse min-w-[1600px]">
          <thead className="text-[10px] font-semibold uppercase tracking-wider sticky top-0 z-10 text-[#737373] bg-[#F8F9FA]">
            <tr>
              <th className="px-2 py-3 w-10 border-b border-[#E5E5E5]">
                <input
                  type="checkbox"
                  data-testid="select-all-plans"
                  checked={selectedPlans.size > 0 && selectedPlans.size === filteredData.length}
                  onChange={toggleSelectAll}
                  className="w-3.5 h-3.5 rounded border-[#E5E5E5] text-[#171717] focus:ring-[#171717]/20 cursor-pointer"
                />
              </th>
              {headers.map((h) => (
                <th 
                  key={h.key} 
                  className={`px-4 py-3 cursor-pointer group transition-colors whitespace-nowrap border-b border-[#E5E5E5]
                    hover:bg-[#F0F0F0]
                    ${h.align === 'right' ? 'text-right' : h.align === 'center' ? 'text-center' : 'text-left'}`}
                  onClick={() => handleSort(h.key)}
                >
                  <div className={`flex items-center ${h.align === 'right' ? 'justify-end' : h.align === 'center' ? 'justify-center' : 'justify-start'}`}>
                    {h.label}
                    <SortIcon field={h.key} />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F5F5F5]">
            {filteredData.map((row) => (
              <tr key={row.id} className={`transition-colors duration-150 ${selectedPlans.has(row.id) ? 'bg-red-50/50' : 'hover:bg-[#FAFAFA]'}`}>
                <td className="px-2 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={selectedPlans.has(row.id)}
                    onChange={() => toggleSelectPlan(row.id)}
                    className="w-3.5 h-3.5 rounded border-[#E5E5E5] text-red-500 focus:ring-red-500/20 cursor-pointer"
                  />
                </td>
                {headers.map((h) => {
                  const value = row[h.key];
                  
                  // Special rendering for specific columns
                  if (h.key === 'id') {
                    return (
                      <td key={h.key} className="px-4 py-3 font-semibold text-[#171717] text-sm">
                        {viewMode === 'group' && <Layers className="w-3 h-3 inline mr-2 text-blue-500" />}
                        {value}
                      </td>
                    );
                  }
                  
                  if (h.key === 'manager') {
                    return (
                      <td key={h.key} className="px-4 py-3 truncate max-w-[150px] text-[#525252] text-sm">
                        {value}
                      </td>
                    );
                  }
                  
                  if (h.key === 'planCount' && viewMode === 'group') {
                    return (
                      <td key={h.key} className="px-4 py-3 text-center">
                        <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${value > 1 ? 'bg-blue-50 text-blue-600' : 'bg-[#F5F5F5] text-[#737373]'}`}>
                          {value}
                        </span>
                      </td>
                    );
                  }
                  
                  if (h.key === 'propertyName') {
                    return (
                      <td key={h.key} className="px-4 py-3 max-w-[180px] truncate text-[#525252] text-sm" title={value || ''}>
                        {value || <span className="text-[#A3A3A3] italic">-</span>}
                      </td>
                    );
                  }
                  
                  if (h.key === 'occupiableLots' || h.key === 'totalOccupiableLots') {
                    return (
                      <td key={h.key} className="px-4 py-3 text-center text-sm">
                        <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${value > 0 ? 'bg-sky-50 text-sky-600' : 'bg-[#F5F5F5] text-[#A3A3A3]'}`}>
                          {value || '-'}
                        </span>
                      </td>
                    );
                  }
                  
                  if (h.key === 'revWorkloadVariance' || h.key === 'grossMarginPerLot' || h.key === 'marginPerLot') {
                    return (
                      <td key={h.key} className={`px-5 py-4 text-right font-mono text-xs font-bold ${value >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {value !== 0 ? `$${value.toLocaleString(undefined, {maximumFractionDigits: 0})}` : '-'}
                      </td>
                    );
                  }
                  
                  if (h.key === 'complianceScore') {
                    return (
                      <td key={h.key} className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold
                          ${value >= 90 ? 'bg-emerald-50 text-emerald-700' : 
                            value >= 75 ? 'bg-blue-50 text-blue-600' : 
                            'bg-red-50 text-red-600'}`}>
                          {value?.toFixed(0) || '-'}%
                        </span>
                      </td>
                    );
                  }
                  
                  if (h.key === 'npsScore') {
                    return (
                      <td key={h.key} className="px-4 py-3 text-center font-semibold text-[#171717] text-sm">
                        {value !== null && value !== undefined ? value : '-'}
                      </td>
                    );
                  }
                  
                  if (h.key === 'persona') {
                    return (
                      <td key={h.key} className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold border ${PERSONA_COLOR_CLASSES[value] || PERSONA_COLOR_CLASSES['Unknown']}`}>
                          {value}
                        </span>
                      </td>
                    );
                  }
                  
                  if (h.format === 'currency') {
                    return (
                      <td key={h.key} className={`px-4 py-3 text-right font-mono text-xs ${h.key === 'revenue' ? 'font-semibold text-[#171717]' : 'text-[#525252]'}`}>
                        {value !== 0 && value !== undefined ? `$${value.toLocaleString(undefined, {maximumFractionDigits: 0})}` : '-'}
                      </td>
                    );
                  }
                  
                  if (h.format === 'percent') {
                    return (
                      <td key={h.key} className="px-4 py-3 text-right font-mono text-xs text-[#525252]">
                        {value !== undefined && value !== null ? `${(value * 100).toFixed(1)}%` : '—'}
                      </td>
                    );
                  }
                  
                  if (h.format === 'number') {
                    return (
                      <td key={h.key} className="px-4 py-3 text-center font-mono text-xs text-[#525252]">
                        {value !== undefined && value !== null ? value.toLocaleString() : '-'}
                      </td>
                    );
                  }
                  
                  return (
                    <td key={h.key} className="px-4 py-3 text-[#525252] text-sm">
                      {value || '-'}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
      <div className="p-3 border-t border-[#E5E5E5] text-[10px] font-medium text-center uppercase tracking-wider text-[#A3A3A3] bg-[#FAFAFA]">
        Displaying {filteredData.length} of {activeData.length} {viewMode === 'group' ? 'Groups' : 'Records'}
      </div>
    </div>
  );
};

export default DataTable;
